import { useEffect, useMemo, useState } from 'react';
import type { TreeNode } from '../../lib/runAssets';
import { runAssetsApi } from '../../lib/runAssets';

interface Props {
  runId: string;
  acceptedExtensions: string[];
  onSelect: (path: string) => void;
  onClose: () => void;
  title: string;
}

interface FlatItem {
  path: string;
  name: string;
  size?: number;
}

/**
 * Dialog for picking a manifest (or hashes.log) file from inside the run's
 * extracted source tree. We flatten the tree and filter by extension; the
 * Phase 3 spec says "system scans extracted contents for candidate manifest
 * files", so a flat searchable list is more useful than a directory chooser.
 */
export function ManifestPicker({ runId, acceptedExtensions, onSelect, onClose, title }: Props) {
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    runAssetsApi
      .loadFileTree(runId)
      .then(setTree)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [runId]);

  const candidates = useMemo<FlatItem[]>(() => {
    if (!tree) return [];
    const accept = acceptedExtensions.map((e) => e.toLowerCase());
    const out: FlatItem[] = [];
    const visit = (node: TreeNode) => {
      if (node.type === 'file') {
        const lower = node.name.toLowerCase();
        if (accept.some((ext) => lower.endsWith(ext))) {
          out.push({ path: node.path, name: node.name, size: node.size });
        }
      }
      node.children?.forEach(visit);
    };
    visit(tree);
    out.sort((a, b) => a.path.localeCompare(b.path));
    return out;
  }, [tree, acceptedExtensions]);

  const filtered = filter
    ? candidates.filter((c) => c.path.toLowerCase().includes(filter.toLowerCase()))
    : candidates;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(720px, 96vw)' }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <strong>{title}</strong>
          <span className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
        <input
          placeholder="Filter by path..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ width: '100%', marginBottom: 8 }}
        />
        {loading && <p className="muted">Loading file tree...</p>}
        {error && <p style={{ color: 'var(--bad)' }}>{error}</p>}
        {!loading && !error && candidates.length === 0 && (
          <p className="muted">
            No matching files found under the extracted source. Accepted extensions:{' '}
            {acceptedExtensions.join(', ')}.
          </p>
        )}
        {filtered.length > 0 && (
          <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
            {filtered.map((item) => (
              <div
                key={item.path}
                className="manifest-pick-row"
                onClick={() => onSelect(item.path)}
              >
                <span className="mono small">{item.path}</span>
                {item.size !== undefined && (
                  <span className="muted small">{(item.size / 1024).toFixed(1)} kB</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
